import { Box, Theme, makeStyles } from '@material-ui/core';
import { get } from 'lodash';
import {
  Children,
  cloneElement,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
} from 'react';
import { TreeNodeMetadata } from './types';

type StyleProps = {
  color?: string;
};

const useStyles = makeStyles<Theme, StyleProps>(theme => ({
  horizontalLine: {
    background: props => props.color || theme.palette.grey[300],
    position: 'absolute',
    height: '2px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  verticalLine: {
    background: props => props.color || theme.palette.grey[300],
    position: 'absolute',
    width: '2px',
  },
  dot: {
    borderRadius: '100%',
    background: props => props.color || theme.palette.grey[300],
    width: '6px',
    height: '6px',
    marginRight: '-4px',
    zIndex: 99,
  },
}));

interface Props {
  children?: ReactNode;
  childrenField?: string;
  root: JSX.Element;
  left?: number;
  gap?: number;
  color?: string;
  top?: number;
  containerClassName?: string;
}

type TreeNodeProps = Omit<Props, 'root' | 'children'> &
  TreeNodeMetadata & {
    node: ReactElement;
  };

export const WbTreeNode: FC<TreeNodeProps> = ({
  left = 20,
  gap = 10,
  node,
  color,
  top = 20,
  ancestorsLastChild,
}) => {
  const classes = useStyles({ color });

  const getVerticalLineStyle = useCallback(
    ({
      lastChild,
      depth,
      maxDepth,
    }: {
      lastChild: boolean;
      depth: number;
      maxDepth: number;
    }) => {
      let height = top ? top + gap : `calc(50% + ${gap / 2}px)`;
      if (!lastChild) height = '100%';
      // we start from the first line on the left (depth 0) so that will be the most distant relative to the element
      const distanceMultiplier = maxDepth - depth;
      return {
        height,
        left: -(left * distanceMultiplier + left / 2),
      } as React.CSSProperties;
    },
    [gap, left, top],
  );

  const getHorizontalLineStyle = useCallback(() => {
    const height = top ? top + gap : `calc(50% + ${gap / 2}px)`;
    return {
      top: height,
      left: -(left / 2),
      width: left / 2,
    } as React.CSSProperties;
  }, [gap, left, top]);

  if (ancestorsLastChild.length === 0) {
    return node;
  }

  return (
    <Box
      position="relative"
      style={{ marginLeft: left * ancestorsLastChild.length }}
    >
      {ancestorsLastChild.map((lastChild, index) => {
        // skip drawing lines for intermediate levels if current path is the last child
        if (lastChild && index !== ancestorsLastChild.length - 1) return null;
        return (
          <Box
            className={classes.verticalLine}
            style={getVerticalLineStyle({
              lastChild,
              depth: index + 1,
              maxDepth: ancestorsLastChild.length,
            })}
          />
        );
      })}
      <Box className={classes.horizontalLine} style={getHorizontalLineStyle()}>
        <Box className={classes.dot} />
      </Box>

      <div style={{ paddingTop: gap }}>{node}</div>
    </Box>
  );
};

export const WbTree: FC<Props> = props => {
  const children = Children.toArray(props.children);
  return (
    <Box className={props.containerClassName}>
      {props.root}
      {children.map((child, i) => (
        <WbTreeNode
          ancestorsLastChild={[i === children.length - 1]}
          key={`${(child as ReactElement)?.key ?? i}-content`}
          node={child as ReactElement}
          {...props}
        />
      ))}
    </Box>
  );
};

/**
 * WbTreeFromData
 * Generic data-driven tree builder.
 *
 * T = type of data elements (e.g. the type of "entity" / "descendants")
 *
 * Props:
 * - rootData: the root element for the current level
 * - data: array of children for the rootData (first level nodes)
 * - childrenField: path to read children from an item (default "children")
 * - renderNode: function that transforms an item into ReactElement (used for each node)
 * - renderRoot: optional function specific for the root (otherwise renderNode is used)
 * - getKey: optional function to extract the key from an item (default index)
 */
interface WbTreeFromDataProps<T> extends Omit<Props, 'root' | 'children'> {
  rootData: T;
  data?: T[];
  childrenField?: string;
  renderNode: (item: T) => ReactElement;
  renderRoot?: (rootData: T) => ReactElement;
  getKey?: (item: T) => React.Key;
  containerClassName?: string;
}

export function WbTreeFromData<T>({
  rootData,
  data = [],
  childrenField = 'children',
  renderNode,
  renderRoot,
  getKey,
  left,
  gap,
  color,
  top,
  containerClassName,
}: WbTreeFromDataProps<T>) {
  const buildChild = (item: T, index: number): ReactElement => {
    const kids = (get(item as any, childrenField) || []) as T[];

    const key = getKey
      ? getKey(item)
      : get(item as any, 'id') ?? get(item as any, 'externalId') ?? index;

    const nodeElement = renderNode(item);

    if (kids && kids.length > 0) {
      return (
        <WbTree
          containerClassName={containerClassName}
          gap={gap}
          left={left}
          top={top}
          color={color}
          root={cloneElement(nodeElement, { key })}
          children={kids.map((k, i) => buildChild(k, i))}
        />
      );
    }

    return cloneElement(nodeElement, { key });
  };

  const rootElement = renderRoot ? renderRoot(rootData) : renderNode(rootData);

  return (
    <WbTree
      containerClassName={containerClassName}
      left={left}
      gap={gap}
      color={color}
      top={top}
      root={rootElement}
      children={data.map((d, i) => buildChild(d, i))}
    />
  );
}

export default WbTree;
